/**
 * RepoLimitModal — shown when a connect is blocked by the plan's
 * connected-repo limit (SYD-B001), or when clicking Connect while
 * already at the limit. The conversion moment: primary CTA books a
 * demo; the escape hatch is disconnect-to-swap.
 */

import { X, GitBranch, ExternalLink } from 'lucide-react';
import { BOOK_DEMO_URL } from './PlanBadge';

export function RepoLimitModal({
  limit,
  plan,
  onClose,
}: {
  limit: number;
  plan: string;
  onClose: () => void;
}) {
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="rounded-lg bg-blue-100 dark:bg-blue-900/50 p-2">
            <GitBranch className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {limit === 1
              ? "You've used your free repo"
              : `You've connected all ${limit} repos on the ${planLabel} plan`}
          </h2>
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
          SystemDox {planLabel} includes {limit} connected{' '}
          {limit === 1 ? 'repository' : 'repositories'}. Book a demo to connect
          your whole organisation — or disconnect a repo to swap.
        </p>

        <div className="flex items-center gap-3">
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Book a demo <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
